import { useState, useEffect, useMemo, useCallback } from 'react';
import { Headphones, Clock, CheckCircle, AlertTriangle, Plus, AlertCircle, LayoutList, Columns3, XCircle, Download } from 'lucide-react';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { BulkActionBar } from '@/components/ui/BulkActionBar';
import { FilterBar, type ActiveFilter, type FilterField } from '@/components/ui/FilterBar';
import { KanbanBoard, type KanbanColumn } from '@/components/ui/KanbanBoard';
import { StatsCard } from '@/components/ui/StatsCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { TicketStatusBadge } from '@/components/modules/TicketStatusBadge';
import { ChannelIcon } from '@/components/modules/ChannelIcon';
import { formatRelative } from '@/lib/format';
import { exportToCSV } from '@/lib/csv';
import { TicketSheet } from '@/components/sheets/TicketSheet';
import { useSupportStore } from '@/stores/supportStore';
import { useSelection } from '@/hooks/useSelection';
import { toast } from '@/stores/toastStore';
import type { SupportTicket } from '@nexus/shared/types';

const priorityVariant: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  urgent: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

const SUPPORT_FILTER_FIELDS: FilterField[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'waiting_customer', label: 'Waiting Customer' },
      { value: 'waiting_internal', label: 'Waiting Internal' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    options: [
      { value: 'urgent', label: 'Urgent' },
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    key: 'channel',
    label: 'Channel',
    type: 'select',
    options: [
      { value: 'email', label: 'Email' },
      { value: 'voice', label: 'Voice' },
      { value: 'sms', label: 'SMS' },
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'live_chat', label: 'Live Chat' },
    ],
  },
];

const KANBAN_COLUMNS: KanbanColumn[] = [
  { id: 'open', title: 'Open', color: 'bg-blue-500' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-amber-500' },
  { id: 'waiting_customer', title: 'Waiting', color: 'bg-purple-500' },
  { id: 'resolved', title: 'Resolved', color: 'bg-emerald-500' },
  { id: 'closed', title: 'Closed', color: 'bg-surface-500' },
];

type ViewMode = 'table' | 'board';
const VIEW_STORAGE_KEY = 'nexus:support-view';

function getInitialView(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === 'table' || stored === 'board') return stored;
  } catch { /* ignore */ }
  return 'table';
}

export function SupportOps() {
  const {
    tickets,
    total,
    isLoading,
    error,
    fetch: fetchTickets,
    create: createTicket,
    update: updateTicket,
    bulkUpdate: bulkUpdateTickets,
    clearError,
  } = useSupportStore();

  const [viewMode, setViewMode] = useState<ViewMode>(getInitialView);
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    channel: 'email',
  });

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const filtered = useMemo(() => {
    if (filters.length === 0) return tickets;
    return tickets.filter((ticket) => {
      return filters.every((f) => {
        if (!f.value) return true;
        const val = String((ticket as unknown as Record<string, unknown>)[f.key] ?? '').toLowerCase();
        const filterVal = f.value.toLowerCase();
        switch (f.operator) {
          case 'eq': return val === filterVal;
          case 'neq': return val !== filterVal;
          case 'contains': return val.includes(filterVal);
          case 'gt': return Number(val) > Number(f.value);
          case 'lt': return Number(val) < Number(f.value);
          default: return true;
        }
      });
    });
  }, [tickets, filters]);

  const { selected, toggle, toggleAll, clear: clearSelection, isAllSelected, count: selectionCount } = useSelection(filtered);

  const openCount = useMemo(
    () => tickets.filter((t) => t.status === 'open' || t.status === 'in_progress').length,
    [tickets],
  );
  const resolvedCount = useMemo(
    () => tickets.filter((t) => t.status === 'resolved' || t.status === 'closed').length,
    [tickets],
  );
  const slaBreaches = useMemo(
    () => tickets.filter((t) => {
      if (!t.slaDeadline) return false;
      const remaining = new Date(t.slaDeadline).getTime() - Date.now();
      return remaining < 0 && t.status !== 'resolved' && t.status !== 'closed';
    }).length,
    [tickets],
  );
  const resolutionRate = useMemo(() => {
    if (tickets.length === 0) return '0%';
    return `${((resolvedCount / tickets.length) * 100).toFixed(1)}%`;
  }, [tickets, resolvedCount]);

  const handleCreate = async () => {
    if (!createForm.subject.trim() || !createForm.description.trim()) return;
    setCreating(true);
    try {
      await createTicket({
        subject: createForm.subject,
        description: createForm.description,
        priority: createForm.priority,
        channel: createForm.channel,
      });
      setCreateOpen(false);
      setCreateForm({ subject: '', description: '', priority: 'medium', channel: 'email' });
      toast.success('Ticket created');
    } catch {
      // error set in store
    } finally {
      setCreating(false);
    }
  };

  const handleBulkClose = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await bulkUpdateTickets(Array.from(selected), { status: 'closed' });
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkChangePriority = async (newPriority: string) => {
    if (selected.size === 0 || !newPriority) return;
    setBulkLoading(true);
    try {
      await bulkUpdateTickets(Array.from(selected), { priority: newPriority });
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleView = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_STORAGE_KEY, mode); } catch { /* ignore */ }
  }, []);

  const handleKanbanMove = useCallback(
    (itemId: string, newColumnId: string) => {
      updateTicket(itemId, { status: newColumnId });
    },
    [updateTicket],
  );

  const getTicketColumnId = useCallback(
    (ticket: SupportTicket) => {
      // Map waiting_internal to the same kanban column as waiting_customer
      if (ticket.status === 'waiting_internal') return 'waiting_customer';
      return ticket.status;
    },
    [],
  );

  const renderTicketCard = useCallback(
    (ticket: SupportTicket) => {
      const slaBreached =
        ticket.slaDeadline &&
        new Date(ticket.slaDeadline).getTime() < Date.now() &&
        ticket.status !== 'resolved' &&
        ticket.status !== 'closed';

      return (
        <div className="space-y-2">
          <p className="text-sm text-surface-200 leading-snug">
            {ticket.subject.length > 50
              ? ticket.subject.slice(0, 50) + '...'
              : ticket.subject}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={priorityVariant[ticket.priority] || 'default'}>
              {ticket.priority}
            </Badge>
            <ChannelIcon channel={ticket.channel} />
            {slaBreached && (
              <span className="text-[10px] font-semibold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                SLA Breached
              </span>
            )}
          </div>
          <p className="text-xs text-surface-400">
            {ticket.assignee || 'Unassigned'}
          </p>
        </div>
      );
    },
    [],
  );

  const columns: Column<SupportTicket>[] = [
    {
      key: 'id',
      header: 'ID',
      render: (row) => <span className="text-xs font-mono text-surface-300">{row.id.slice(0, 8)}</span>,
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => <p className="text-sm text-surface-200 truncate max-w-xs">{row.subject}</p>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <TicketStatusBadge status={row.status} />,
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (row) => <Badge variant={priorityVariant[row.priority] || 'default'}>{row.priority}</Badge>,
    },
    {
      key: 'channel',
      header: 'Channel',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <ChannelIcon channel={row.channel} />
          <span className="text-xs text-surface-400 capitalize">{row.channel.replace('_', ' ')}</span>
        </div>
      ),
    },
    {
      key: 'assignee',
      header: 'Assignee',
      render: (row) => (
        <span className="text-sm text-surface-300">{row.assignee || '--'}</span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400">{formatRelative(row.createdAt)}</span>,
    },
    {
      key: 'sla',
      header: 'SLA',
      render: (row) => {
        if (!row.slaDeadline) return <span className="text-xs text-surface-500">--</span>;
        const remaining = new Date(row.slaDeadline).getTime() - Date.now();
        const breached = remaining < 0;
        return (
          <span className={`text-xs font-medium ${breached ? 'text-red-400' : 'text-surface-400'}`}>
            {breached ? 'Breached' : `${Math.floor(remaining / 3600_000)}h left`}
          </span>
        );
      },
    },
  ];

  if (isLoading && tickets.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-44 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        {/* Stats — 4 cards */}
        <SkeletonStats count={4} />
        {/* Filter selects skeleton */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-44 rounded-lg" />
          <Skeleton className="h-10 w-40 rounded-lg" />
        </div>
        {/* Table — 8 columns */}
        <SkeletonTable rows={7} cols={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Support Operations</h2>
          <p className="text-sm text-surface-400 mt-0.5">Manage and resolve support tickets across all channels</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={() =>
              exportToCSV(
                filtered,
                [
                  { key: 'subject', header: 'Subject' },
                  { key: 'status', header: 'Status' },
                  { key: 'priority', header: 'Priority' },
                  { key: 'channel', header: 'Channel' },
                  { key: 'assignee', header: 'Assignee' },
                  { key: 'createdAt', header: 'Created At' },
                ],
                `nexus-tickets-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={filtered.length === 0}
          >
            Export CSV
          </Button>
          <Button icon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setCreateOpen(true)}>
            Create Ticket
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="ml-auto text-xs text-red-400 hover:text-red-300">Dismiss</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="Open Tickets" value={String(openCount)} icon={<Headphones className="h-5 w-5" />} />
        <StatsCard label="Total Tickets" value={String(total || tickets.length)} icon={<Clock className="h-5 w-5" />} />
        <StatsCard label="Resolution Rate" value={resolutionRate} icon={<CheckCircle className="h-5 w-5" />} />
        <StatsCard label="SLA Breaches" value={String(slaBreaches)} icon={<AlertTriangle className="h-5 w-5" />} />
      </div>

      {/* Filters + View Toggle */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <FilterBar
            fields={SUPPORT_FILTER_FIELDS}
            filters={filters}
            onChange={setFilters}
            storageKey="nexus:support-filters"
          />
        </div>
        <div className="flex items-center bg-surface-800 border border-surface-700 rounded-lg p-0.5 shrink-0">
          <button
            onClick={() => toggleView('table')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-surface-700 text-surface-100'
                : 'text-surface-400 hover:text-surface-300'
            }`}
            title="Table view"
          >
            <LayoutList className="h-3.5 w-3.5" />
            Table
          </button>
          <button
            onClick={() => toggleView('board')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'board'
                ? 'bg-surface-700 text-surface-100'
                : 'text-surface-400 hover:text-surface-300'
            }`}
            title="Board view"
          >
            <Columns3 className="h-3.5 w-3.5" />
            Board
          </button>
        </div>
      </div>

      {/* Ticket Table / Board */}
      {viewMode === 'table' ? (
        <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
          {filtered.length > 0 ? (
            <DataTable
              columns={columns}
              data={filtered}
              onRowClick={(row) => setSelectedId(row.id)}
              selectable
              selectedIds={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              isAllSelected={isAllSelected}
            />
          ) : (
            <EmptyState
              icon={<Headphones className="h-8 w-8" />}
              title="No tickets found"
              description={tickets.length === 0
                ? 'Tickets will appear here once customers reach out.'
                : 'No tickets match the current filters.'}
            />
          )}
        </div>
      ) : (
        filtered.length > 0 ? (
          <KanbanBoard
            columns={KANBAN_COLUMNS}
            items={filtered}
            getColumnId={getTicketColumnId}
            renderCard={renderTicketCard}
            onMove={handleKanbanMove}
          />
        ) : (
          <div className="bg-surface-800 border border-surface-700 rounded-lg">
            <EmptyState
              icon={<Headphones className="h-8 w-8" />}
              title="No tickets found"
              description={tickets.length === 0
                ? 'Tickets will appear here once customers reach out.'
                : 'No tickets match the current filters.'}
            />
          </div>
        )
      )}

      {/* Create Ticket Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Ticket">
        <div className="space-y-4">
          <Input
            label="Subject"
            placeholder="Brief description of the issue"
            value={createForm.subject}
            onChange={(e) => setCreateForm((f) => ({ ...f, subject: e.target.value }))}
          />
          <div className="w-full">
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Description</label>
            <textarea
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent min-h-[120px] resize-y"
              placeholder="Detailed description of the issue..."
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Priority"
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
              value={createForm.priority}
              onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
            />
            <Select
              label="Channel"
              options={[
                { value: 'email', label: 'Email' },
                { value: 'voice', label: 'Voice' },
                { value: 'sms', label: 'SMS' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'live_chat', label: 'Live Chat' },
              ]}
              value={createForm.channel}
              onChange={(e) => setCreateForm((f) => ({ ...f, channel: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreate}
              loading={creating}
              disabled={!createForm.subject.trim() || !createForm.description.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Ticket Detail Sheet */}
      <TicketSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />

      {/* Bulk Action Bar */}
      <BulkActionBar count={selectionCount} onClear={clearSelection}>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkClose}
          loading={bulkLoading}
          icon={<XCircle className="h-3.5 w-3.5" />}
        >
          Close
        </Button>
        <select
          className="px-2 py-1 text-xs bg-surface-700 border border-surface-600 rounded-md text-surface-200 focus:outline-none focus:ring-1 focus:ring-nexus-500"
          defaultValue=""
          onChange={(e) => { handleBulkChangePriority(e.target.value); e.target.value = ''; }}
        >
          <option value="" disabled>Change Priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </BulkActionBar>
    </div>
  );
}
